
import React from 'react';
import { BuildingType, TileData, Grid, ZoneType } from '../../types';
import { BUILDINGS, GRID_SIZE } from '../../constants';
import { playUiSfx } from '../../utils/uiAudio';
import { surfaces, type as typeTokens, buttons, infoCards, cn } from './kit';

interface InspectorPanelProps {
    tile: TileData;
    grid: Grid;
    onUpgrade: () => void;
    onDelete: () => void;
    budget: number;
    hasJumpGate: boolean;
    currentMaxLevel: number; // passed from UIOverlay
}

const InspectorPanel: React.FC<InspectorPanelProps> = ({ tile, grid, onUpgrade, onDelete, budget, hasJumpGate, currentMaxLevel }) => {
    const config = BUILDINGS[tile.buildingType];
    const level = tile.level || 1;
    const upgradeCost = Math.floor(config.cost * level * 1.2);
    const canAfford = budget >= upgradeCost;
    const isCore = tile.buildingType === BuildingType.Core;
    const isMaxLevel = level >= 5;
    
    // Check if player has researched the capability to upgrade further
    // If level is 1, need maxLevel >= 2 to upgrade.
    const isResearchCapped = level >= currentMaxLevel;
    
    // Logic Calculation for Display
    const multiplier = 1 + ((level - 1) * 0.15);
    let projectedBudget = Math.floor(config.budgetGen * multiplier);
    const projectedCompliance = Math.floor(config.complianceGen * multiplier);
    const projectedThreat = Math.floor(config.threatGen * multiplier);

    // Road Bonus Check
    let roadBonus = false;
    if (config.zone === ZoneType.Ops || config.zone === ZoneType.Public) {
        const directions = [[0,1], [0,-1], [1,0], [-1,0]];
        for (const [dx, dy] of directions) {
            const nx = tile.x + dx;
            const ny = tile.y + dy;
            if (nx >= 0 && nx < GRID_SIZE && ny >= 0 && ny < GRID_SIZE) {
                if (grid[ny][nx].buildingType === BuildingType.Road) {
                    roadBonus = true;
                    break;
                }
            }
        }
    }
    
    const hasLoadBalancer = grid.flat().some(t => t.buildingType === BuildingType.LoadBalancer && t.isConnected);
    const lbPenalty = config.zone === ZoneType.Public && !hasLoadBalancer;

    if (projectedBudget > 0) {
        if (lbPenalty) projectedBudget = Math.floor(projectedBudget * 0.3);
        if (roadBonus) projectedBudget = Math.floor(projectedBudget * 1.2);
    }

    return (
        <div className="absolute bottom-28 left-2 md:left-4 z-40 pointer-events-auto animate-slide-in-right max-w-[min(18rem,calc(100vw-5rem))]">
            <div className={cn(surfaces.panel, 'w-full p-3 md:p-4')}>
                <div className="flex justify-between items-start mb-3 border-b border-slate-700/50 pb-2">
                    <div>
                        <h3 className={typeTokens.titleLg}>{config.name}</h3>
                        <p className={cn(typeTokens.eyebrow, 'text-slate-400 mt-1')}>{config.category} • Lvl {level}</p>
                    </div>
                    <button onClick={() => { playUiSfx('click'); onDelete(); }} className={buttons.icon} title="Dismantle">
                        🗑️
                    </button>
                </div>
                
                <p className={cn(typeTokens.body, 'text-xs mb-4 italic leading-relaxed')}>{config.description}</p>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className={cn(surfaces.panelInset, 'p-2')}>
                        <div className={infoCards.enablesLabel}>Output</div>
                        <div className={cn(typeTokens.mono, 'font-bold', projectedBudget >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                            {projectedBudget >= 0 ? '+' : ''}{projectedBudget} <span className="text-[9px]">$</span>
                        </div>
                    </div>
                    <div className={cn(surfaces.panelInset, 'p-2')}>
                        <div className={infoCards.enablesLabel}>Compliance</div>
                        <div className={cn(typeTokens.mono, 'font-bold', projectedCompliance >= 0 ? 'text-cyan-400' : 'text-red-400')}>
                             {projectedCompliance >= 0 ? '+' : ''}{projectedCompliance}
                        </div>
                    </div>
                    <div className={cn(surfaces.panelInset, 'p-2')}>
                        <div className={infoCards.enablesLabel}>Threat Impact</div>
                        <div className={cn(typeTokens.mono, 'font-bold', projectedThreat <= 0 ? 'text-emerald-400' : 'text-red-400')}>
                             {projectedThreat > 0 ? '+' : ''}{projectedThreat}
                        </div>
                    </div>
                    <div className={cn(surfaces.panelInset, 'p-2')}>
                        <div className={infoCards.enablesLabel}>Status</div>
                        <div className={cn('font-bold text-xs', tile.isConnected ? 'text-emerald-400' : 'text-red-500 animate-pulse')}>
                             {tile.isConnected ? 'ONLINE' : 'OFFLINE'}
                        </div>
                    </div>
                </div>

                {/* Status Messages */}
                {lbPenalty && (
                    <div className={cn(infoCards.value, 'mb-3 py-1 text-[10px] font-bold')}>
                        ⚠ Load Balancer Missing (-70% Revenue)
                    </div>
                )}
                {roadBonus && (
                    <div className={cn(infoCards.enables, 'mb-3 py-1 text-[10px] font-bold')}>
                        ⚡ Road Access Bonus (+20% Revenue)
                    </div>
                )}
                
                {/* Upgrade Action */}
                {!isCore && (
                    <button 
                        onClick={() => { playUiSfx('build'); onUpgrade(); }}
                        disabled={isMaxLevel || !canAfford || isResearchCapped}
                        className={cn(
                            'w-full rounded text-xs font-bold uppercase tracking-widest transition-all mb-2 flex justify-between px-4',
                            isMaxLevel || !canAfford || isResearchCapped
                                ? 'bg-slate-800 text-slate-500 cursor-not-allowed py-2'
                                : cn(buttons.primary, 'py-2 bg-indigo-600 hover:bg-indigo-500 shadow-lg'),
                        )}
                    >
                        <span>{isMaxLevel ? 'MAX LEVEL' : 'UPGRADE'}</span>
                        {!isMaxLevel && <span>${upgradeCost}</span>}
                    </button>
                )}
                
                {/* Warning about Research Cap */}
                {isResearchCapped && !isMaxLevel && !isCore && (
                    <div className="text-[9px] text-red-400 text-center font-bold uppercase">
                        Requires Additional Research
                    </div>
                )}
            </div>
        </div>
    );
};

export default InspectorPanel;